import moment from 'moment';
import { useTranslation } from 'react-i18next';

import * as api from '../../api';
import {
  MONTH_DATE_TIME_FORMAT,
  TIME_OF_DAY_FORMAT,
  MONTH_DATE_FORMAT,
} from '../../utils/dateTime';

import styles from './index.module.css';

interface Props {
  startTime: Date;
  timezone: string;
  arrivalTime?: Date;
  status?: api.EhrTimeSlot.AppointmentStatus;
  displayTime: boolean;
}

const AppointmentStatus: React.FC<Props> = ({
  timezone,
  status,
  startTime,
  arrivalTime,
  displayTime,
}) => {
  const { t } = useTranslation();
  const arrivalTimeView = arrivalTime && (
    <span>
      {t('(Arrive: {{time}})', {
        time: moment(arrivalTime).tz(timezone).format(TIME_OF_DAY_FORMAT),
      })}
    </span>
  );
  const retrieveLabel = () => {
    switch (status) {
      case api.EhrTimeSlot.AppointmentStatus.Cancelled:
        return <span className={styles.CanceledLabel}>{t('(Canceled)')}</span>;
      case api.EhrTimeSlot.AppointmentStatus.Confirmed:
        return (
          <>
            {arrivalTimeView}
            <span className={styles.ConfirmedLabel}>{t('(Confirmed)')}</span>
          </>
        );
      case api.EhrTimeSlot.AppointmentStatus.Rescheduled:
        return (
          <span className={styles.RescheduledLabel}>{t('(Rescheduled)')}</span>
        );
      case undefined:
        return displayTime ? arrivalTimeView : '';
      default:
        return '';
    }
  };
  return (
    <span className={styles.ScheduleLabel}>
      {!moment(arrivalTime).isSame(startTime, 'minute') && retrieveLabel()}
    </span>
  );
};

/**
 * Display encounter time info including start time, arrival time, or cancel status
 */
export const AppointmentTimeView: React.FC<Props> = ({
  timezone,
  status,
  startTime,
  arrivalTime,
  displayTime,
}) => {
  const startTimeTz = moment(startTime).tz(timezone);
  const timeFormat = displayTime ? MONTH_DATE_TIME_FORMAT : MONTH_DATE_FORMAT;
  return (
    <div className={styles.Date}>
      <span className={styles.StartTime}>
        {startTimeTz.format(timeFormat)}
        <AppointmentStatus
          timezone={timezone}
          status={status}
          startTime={startTime}
          arrivalTime={arrivalTime}
          displayTime={displayTime}
        />
      </span>
    </div>
  );
};
